import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import axios from '../api/client';
import { useUser } from '../context/UserContext';
import coursesData from './courses.json';
import './course-experience.css';

const pageMotion = {
  initial: { opacity: 0, y: 14 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -10 },
  transition: { duration: 0.28 },
};

function InteractiveLesson({ lesson }) {
  const [active, setActive] = useState(0);
  const signal = lesson.signals[active];

  return (
    <>
      <div className="lessonEyebrow">{lesson.eyebrow}</div>
      <h1>{lesson.heading}</h1>
      <p className="lessonLead">{lesson.intro}</p>
      <div className="signalGrid">
        {lesson.signals.map((item, index) => (
          <motion.button
            type="button"
            className={`signalCard ${active === index ? 'active' : ''}`}
            key={item.label}
            onClick={() => setActive(index)}
            whileTap={{ scale: 0.98 }}
          >
            <div className="signalLabel">{item.label}</div>
            <div className="signalValue">{item.value}</div>
            <div className="signalCaption">{item.caption}</div>
          </motion.button>
        ))}
        <AnimatePresence mode="wait">
          <motion.div className="signalExplanation" key={signal.label} {...pageMotion}>
            <strong>{signal.label}:</strong> {signal.explanation}
          </motion.div>
        </AnimatePresence>
      </div>
      <div className="lessonTakeaway"><strong>Remember:</strong> {lesson.takeaway}</div>
    </>
  );
}

function ComparisonLesson({ lesson }) {
  const [active, setActive] = useState(0);
  const [revealed, setRevealed] = useState(false);

  return (
    <>
      <div className="lessonEyebrow">Try it yourself</div>
      <h1>{lesson.heading}</h1>
      <p className="lessonLead">{lesson.intro}</p>
      <div className="companyGrid">
        {lesson.companies.map((company, index) => (
          <motion.button
            type="button"
            key={company.ticker}
            className={`companyCard ${active === index ? 'active' : ''}`}
            onClick={() => setActive(index)}
            whileTap={{ scale: 0.98 }}
          >
            <span className="companyTicker">{company.ticker}</span>
            <h2>{company.name}</h2>
            <div className="metricRow"><span>Revenue growth</span><strong>{company.growth}%</strong></div>
            <div className="metricRow"><span>FCF margin</span><strong>{company.margin}%</strong></div>
            <div className="metricRow"><span>EV / Sales</span><strong>{company.multiple}x</strong></div>
          </motion.button>
        ))}
      </div>
      <button className="revealButton" type="button" onClick={() => setRevealed(true)}>
        Reveal the takeaway
      </button>
      <AnimatePresence>
        {revealed && (
          <motion.div className="comparisonAnswer" {...pageMotion}>
            <strong>Good analysis allows nuance.</strong><br />{lesson.answer}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function QuizLesson({ lesson, onPassed }) {
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const questions = lesson.questions || [];
  const score = questions.filter(q => answers[q.id] === q.correctAnswer).length;

  const retry = () => {
    setAnswers({});
    setSubmitted(false);
  };

  const submit = () => {
    setSubmitted(true);
    if (score === questions.length) onPassed();
  };

  return (
    <>
      <div className="lessonEyebrow">Knowledge check</div>
      <h1>{lesson.heading || lesson.title}</h1>
      <p className="lessonLead">{lesson.description || 'Choose the best answer.'}</p>
      {questions.map((question, qIndex) => (
        <motion.div className="quizQuestion" key={question.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: qIndex * 0.06 }}>
          <h3>{qIndex + 1}. {question.question}</h3>
          {question.options.map(option => {
            const selected = answers[question.id] === option;
            const stateClass = submitted
              ? option === question.correctAnswer ? 'correct' : selected ? 'wrong' : ''
              : selected ? 'selected' : '';
            return (
              <button
                type="button"
                className={`quizOption ${stateClass}`}
                key={option}
                onClick={() => !submitted && setAnswers(prev => ({ ...prev, [question.id]: option }))}
              >
                <span className="optionDot">{submitted && option === question.correctAnswer ? '✓' : selected ? '•' : ''}</span>
                {option}
              </button>
            );
          })}
          {submitted && <p className="quizFeedback">{question.explanation || (answers[question.id] === question.correctAnswer ? 'Correct.' : `The correct answer is ${question.correctAnswer}.`)}</p>}
        </motion.div>
      ))}
      {submitted && (
        <motion.div className="quizResult" {...pageMotion}>
          <strong>{score}/{questions.length}</strong>
          <div>{score === questions.length ? 'Perfect — you completed the course.' : 'Almost there. Review the explanations and try again.'}</div>
        </motion.div>
      )}
      {!submitted ? (
        <button className="primaryCourseButton" disabled={Object.keys(answers).length !== questions.length} type="button" onClick={submit}>
          Check my answers
        </button>
      ) : score < questions.length ? (
        <button className="primaryCourseButton" type="button" onClick={retry}>Try again</button>
      ) : null}
    </>
  );
}

function CoursePage() {
  const { courseId, lessonId } = useParams();
  const navigate = useNavigate();
  const { user, isLoggedIn } = useUser();
  const course = coursesData.courses.find(item => item.id === courseId);
  const lessons = useMemo(() => course
    ? course.sections.flatMap(section => section.lessons.map(lesson => ({ ...lesson, sectionId: section.id })))
    : [], [course]);
  const currentIndex = Math.max(0, lessons.findIndex(lesson => lesson.id === lessonId));
  const currentLesson = lessons[currentIndex];
  const [completed, setCompleted] = useState([]);

  useEffect(() => {
    if (!lessonId && lessons[0]) navigate(`/courses/${courseId}/${lessons[0].id}`, { replace: true });
  }, [courseId, lessonId, lessons, navigate]);

  useEffect(() => {
    if (!isLoggedIn || !course) return;
    axios.get('https://stocksleuth.xyz:5000/auth/getCourse', { params: { courseId } })
      .then(({ data }) => setCompleted(data.completedLessons || [])).catch(() => {});
  }, [course, courseId, isLoggedIn, user?.username]);

  if (!course) return <div style={{ padding: 30 }}>Course not found.</div>;
  if (!currentLesson) return null;

  const markComplete = id => {
    if (completed.includes(id)) return;
    setCompleted(previous => [...previous, id]);
    if (isLoggedIn) {
      axios.post('https://stocksleuth.xyz:5000/auth/setCourse', {
        courseId, lessonId: id,
      }).catch(() => {});
    }
  };

  const goNext = () => {
    markComplete(currentLesson.id);
    const next = lessons[currentIndex + 1];
    if (next) navigate(`/courses/${courseId}/${next.id}`);
  };

  const progress = Math.round((completed.length / lessons.length) * 100);

  return (
    <div className="coursePage courseExperience" style={{ position: 'absolute', top: 64, left: 0, right: 'var(--sidebar-width, 306px)', bottom: 0, display: 'flex', overflow: 'hidden' }}>
      <main className="courseMain">
        <header className="courseHero">
          <div className="courseHeroTop">
            <button className="courseBack" type="button" onClick={() => navigate('/learn')}>← All courses</button>
            <div className="courseMeta"><span>{course.level || 'Self-paced'}</span><span>•</span><span>{progress}% complete</span></div>
          </div>
          <h2 style={{ margin: '22px 0 0' }}>{course.title}</h2>
          <div className="courseProgress"><div style={{ width: `${progress}%` }} /></div>
        </header>
        <AnimatePresence mode="wait">
          <motion.article className="lessonShell" key={currentLesson.id} {...pageMotion}>
            {currentLesson.type === 'interactive' && <InteractiveLesson lesson={currentLesson} />}
            {currentLesson.type === 'comparison' && <ComparisonLesson lesson={currentLesson} />}
            {currentLesson.type === 'quiz' && <QuizLesson lesson={currentLesson} onPassed={() => markComplete(currentLesson.id)} />}
            {currentLesson.type === 'text' && (
              <div
                className="lessonText"
                dangerouslySetInnerHTML={{ __html: currentLesson.text }}
              />
            )}
            {currentLesson.type === 'video' && <div><h1>{currentLesson.title}</h1><video controls src={currentLesson.videoLink} style={{ width: '100%', borderRadius: 18 }} /></div>}
            {currentLesson.type !== 'quiz' && (
              <div className="lessonActions">
                <button className="secondaryCourseButton" type="button" disabled={currentIndex === 0} onClick={() => navigate(`/courses/${courseId}/${lessons[currentIndex - 1].id}`)}>Previous</button>
                <button className="primaryCourseButton" type="button" onClick={goNext}>{currentIndex === lessons.length - 1 ? 'Complete lesson' : 'Complete & continue →'}</button>
              </div>
            )}
          </motion.article>
        </AnimatePresence>
      </main>
      <aside className="courseNav">
        <div className="courseNavTitle">Course contents</div>
        {course.sections.map(section => (
          <div key={section.id}>
            <div className="courseSectionTitle">{section.title}</div>
            {section.lessons.map((lesson, index) => {
              const done = completed.includes(lesson.id);
              return (
                <button type="button" className={`courseLessonLink ${lesson.id === currentLesson.id ? 'active' : ''}`} key={lesson.id} onClick={() => navigate(`/courses/${courseId}/${lesson.id}`)}>
                  <span className={`lessonStatus ${done ? 'done' : ''}`}>{done ? '✓' : index + 1}</span>
                  <span>{lesson.title}<small style={{ display: 'block', opacity: .65, marginTop: 3 }}>{lesson.time} min</small></span>
                </button>
              );
            })}
          </div>
        ))}
      </aside>
    </div>
  );
}

export default CoursePage;
